// ─── Breakdown card ───────────────────────────────────────────────────
// Single column of name + count(%) + thin progress bar. Per-row color
// override lets the By Status card use the StatusChip palette while the
// other two cards stay neutral copper.

import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";

export interface BreakdownRow {
  key: string;
  count: number;
  to?: string;
  color?: string;
  dotColor?: string;
}

interface BreakdownCardProps {
  title: string;
  rows: BreakdownRow[];
  overallTotal: number;
  defaultBarColor: string;
  emptyHint: string;
  /**
   * One-line statement under the title — the ranking spelled out in
   * words, for the reader who wants the answer without reading the
   * bars. Omit it when there are no rows to state anything about.
   */
  highlight?: ReactNode;
  /**
   * Cap the visible row count to this number; render a "Show more"
   * toggle when more rows exist. Defaults to 5 so cards with many
   * categories don't grow taller than their neighbors. Pass a large
   * number (or Infinity) to always show all rows.
   */
  collapseAfter?: number;
}

export default function BreakdownCard({
  title,
  rows,
  overallTotal,
  defaultBarColor,
  emptyHint,
  highlight,
  collapseAfter = 5,
}: BreakdownCardProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasOverflow = rows.length > collapseAfter;
  const visibleRows =
    hasOverflow && !expanded ? rows.slice(0, collapseAfter) : rows;
  return (
    <Box sx={{ minWidth: 0, overflow: "hidden" }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: highlight ? 0.25 : 1.5 }}
      >
        {title}
      </Typography>
      {highlight && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          {highlight}
        </Typography>
      )}
      {rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {emptyHint}
        </Typography>
      ) : (
        visibleRows.map(({ key, count, to, color, dotColor }) => {
          const pct =
            overallTotal > 0 ? Math.round((count * 100) / overallTotal) : 0;
          const barPct = overallTotal > 0 ? (count / overallTotal) * 100 : 0;
          const interactive = !!to;
          const barColor = color ?? defaultBarColor;
          return (
            <Box
              key={key}
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              onClick={interactive ? () => navigate(to!) : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(to!);
                      }
                    }
                  : undefined
              }
              sx={{
                py: 1.5,
                px: 0.5,
                mx: -0.5,
                borderRadius: 1,
                cursor: interactive ? "pointer" : "default",
                transition: "background-color .15s",
                "&:hover": interactive
                  ? { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                  : undefined,
                "&:focus-visible": interactive
                  ? {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: -2,
                    }
                  : undefined,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                sx={{ mb: 0.5 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {dotColor && (
                    <Box
                      component="span"
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: dotColor,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                    }}
                    title={key}
                  >
                    {key}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  <Box
                    component="span"
                    sx={{ color: "text.primary", fontWeight: 600, mr: 0.5 }}
                  >
                    {count}
                  </Box>
                  {pct}%
                </Typography>
              </Stack>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 2,
                  bgcolor: alpha(barColor, 0.12),
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${barPct}%`,
                    bgcolor: barColor,
                    borderRadius: 2,
                    transition: "width .4s",
                  }}
                />
              </Box>
            </Box>
          );
        })
      )}
      {hasOverflow && (
        <Button
          size="small"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          sx={{
            mt: 0.5,
            alignSelf: "flex-start",
            px: 0.75,
            minHeight: 0,
            color: "primary.dark",
          }}
        >
          {expanded
            ? "Show less"
            : `Show ${rows.length - collapseAfter} more`}
        </Button>
      )}
    </Box>
  );
}
